/**
 * Feb 2026 · Administrator-only Activity Trail viewer for the
 * `audit_log` collection populated by AuditLogMiddleware. Filters by
 * collection / record_id / user_email / verb so ops can trace "who
 * touched what". Reads GET /api/audit-log.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardTextIcon, MagnifyingGlassIcon, ArrowsClockwiseIcon, EyeIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

const verbColor = (v) => ({
  POST:   "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  PUT:    "bg-sky-500/15 text-sky-300 border-sky-500/40",
  PATCH:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
  DELETE: "bg-red-500/15 text-red-300 border-red-500/40",
}[v] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40");

// Mar 1, 2026 · Show timestamps in IST (Asia/Kolkata) so ops don't have
// to mentally add 5h30m when a customer calls about a change.
// Sep 04, 2026 · Rewired to shared `lib/datetime` so audit-log output
// matches the DD-MM-YYYY convention rolled out everywhere else.
import { fmtDateTimeIST, fmtDateTimeSecIST } from "@/lib/datetime";
const fmtIST = (iso, withSeconds = true) =>
  withSeconds ? fmtDateTimeSecIST(iso, iso ? String(iso) : "—") : fmtDateTimeIST(iso, iso ? String(iso) : "—");

// Mar 1, 2026 · Human-readable field labels so the diff table stops
// speaking DB-slang. Fall back to Title Case of the raw key.
const FIELD_LABELS = {
  approval_status: "Approval Status", rejection_reason: "Rejection Reason",
  authorized: "Authorized", authorized_by: "Authorized By", authorized_at: "Authorized At",
  status: "Status", cancel_reason: "Cancellation Reason",
  order_qty: "Order Qty", extra_qty: "Buffer Qty", extra_pct: "Buffer %",
  disp_qty: "Dispatched Qty", cancelled_qty: "Cancelled Qty",
  rate: "Rate", delivery_date: "Delivery Date", priority: "Priority",
  customer_id: "Customer", po_number: "Customer PO No", instructions: "Instructions",
  invoice_no: "Invoice No", invoice_date: "Invoice Date",
  shipping_bill_no: "Shipping Bill No", port_code: "Port Code",
  export_type: "Export Type", fx_rate_to_inr: "FX Rate → INR",
  items: "Line Items", lines: "Line Items", buyer: "Buyer", consignee: "Consignee",
};
const labelFor = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Human-friendly one-liner for any value type. Long strings are trimmed;
// arrays/objects collapse to their shape so the diff row stays scannable.
const summarize = (v) => {
  if (v === undefined || v === null) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return "(empty)";
    if (s.length > 80) return s.slice(0, 80) + "…";
    return s;
  }
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") {
    const keys = Object.keys(v);
    if (keys.length <= 3) return keys.map(k => `${k}: ${summarize(v[k])}`).join(", ").slice(0, 100);
    return `${keys.length} fields`;
  }
  return String(v);
};

// Ignore server-side bookkeeping keys that would otherwise flood every diff.
const _NOISE_KEYS = new Set(["_id", "updated_at", "created_at", "created_by",
  "amendments", "approval_history", "line_history", "amendment_history"]);

// Deep-diff two objects; returns [{ key, path, before, after, kind }]
// kind ∈ { added, removed, changed }. Recurses one level into nested
// objects and does a shallow items[] diff (Mar 1, 2026 · handles the
// "adjust-qty on line" case that used to show "0 changes").
const computeDiff = (before, after) => {
  const rows = [];
  const b = before && typeof before === "object" && !Array.isArray(before) ? before : {};
  const a = after  && typeof after  === "object" && !Array.isArray(after)  ? after  : {};

  // Special-case: CREATE (before is null/empty) — surface the non-noise
  // fields that were populated so the user sees WHAT was created.
  if (!before && after && typeof after === "object") {
    for (const k of Object.keys(after).sort()) {
      if (_NOISE_KEYS.has(k)) continue;
      const v = after[k];
      if (v === undefined || v === null || v === "" || v === 0 ||
          (Array.isArray(v) && v.length === 0)) continue;
      rows.push({ key: k, before: undefined, after: v, kind: "added" });
    }
    return rows;
  }
  // Special-case: DELETE (after is null) — snapshot key identifying fields.
  if (before && !after && typeof before === "object") {
    for (const k of ["invoice_no", "order_no", "doc_no", "po_no", "wo_no", "code", "name", "id", "status"]) {
      if (before[k] !== undefined && before[k] !== "") {
        rows.push({ key: k, before: before[k], after: undefined, kind: "removed" });
      }
    }
    return rows;
  }

  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).sort();
  for (const k of keys) {
    if (_NOISE_KEYS.has(k)) continue;
    const bv = b[k], av = a[k];
    if (JSON.stringify(bv) === JSON.stringify(av)) continue;
    // Line-items array — surface per-line delta so a single qty edit
    // doesn't collapse into an opaque "5 items → 5 items".
    if ((k === "items" || k === "lines") && Array.isArray(bv) && Array.isArray(av)) {
      const maxN = Math.max(bv.length, av.length);
      for (let i = 0; i < maxN; i++) {
        const lb = bv[i] || {}, la = av[i] || {};
        const idLbl = la.item_no || lb.item_no || la.line_no || lb.line_no ||
                      la.article_no || lb.article_no || `#${i + 1}`;
        if (!bv[i]) { rows.push({ key: `${labelFor(k)} · ${idLbl}`, before: undefined, after: la, kind: "added" }); continue; }
        if (!av[i]) { rows.push({ key: `${labelFor(k)} · ${idLbl}`, before: lb, after: undefined, kind: "removed" }); continue; }
        for (const lk of Array.from(new Set([...Object.keys(lb), ...Object.keys(la)])).sort()) {
          if (_NOISE_KEYS.has(lk)) continue;
          if (JSON.stringify(lb[lk]) !== JSON.stringify(la[lk])) {
            rows.push({ key: `${labelFor(k)} · ${idLbl} · ${labelFor(lk)}`, before: lb[lk], after: la[lk], kind: "changed" });
          }
        }
      }
      continue;
    }
    rows.push({ key: k, before: bv, after: av,
                kind: (bv === undefined || bv === null || bv === "") ? "added"
                      : (av === undefined || av === null || av === "") ? "removed"
                      : "changed" });
  }
  return rows;
};

const fmtVal = (v) => {
  if (v === undefined) return <span className="text-zinc-600 italic">—</span>;
  if (v === null) return <span className="text-zinc-600">null</span>;
  return <span className="font-mono break-all">{summarize(v)}</span>;
};

// Mar 1, 2026 · One-line human summary of what a diff row says, so the
// operator can skim without parsing before/after columns.
const changeSentence = (d) => {
  const label = labelFor(String(d.key).split(" · ")[0]);
  const rest = String(d.key).includes(" · ") ? " " + String(d.key).split(" · ").slice(1).join(" · ") : "";
  if (d.kind === "added")   return `${label}${rest} set to “${summarize(d.after)}”`;
  if (d.kind === "removed") return `${label}${rest} cleared (was “${summarize(d.before)}”)`;
  return `${label}${rest} changed: “${summarize(d.before)}” → “${summarize(d.after)}”`;
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ collection: "", record_id: "", user_email: "", verb: "" });
  const [view, setView] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      for (const k of Object.keys(f)) if (f[k]) params[k] = f[k];
      const { data } = await api.get("/audit-log", { params });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const collections = useMemo(() => Array.from(new Set(rows.map(r => r.collection).filter(Boolean))).sort(), [rows]);

  return (
    <>
      <div className="p-6 space-y-5" data-testid="audit-log-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Security · Administrator</div>
            <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
              <ClipboardTextIcon size={26} weight="fill" className="text-[var(--accent)]" />
              Activity Trail
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Every mutating request captured by the audit middleware — verb, path, user, before / after snapshots, and reason.
            </p>
          </div>
          <button data-testid="audit-refresh" onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" className={loading ? "animate-spin" : ""} />Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="tactical-card p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Collection</div>
            <select data-testid="audit-f-collection" value={f.collection} onChange={e => setF({ ...f, collection: e.target.value })} className={inp}>
              <option value="">All</option>
              {collections.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Record ID</div>
            <input data-testid="audit-f-record" value={f.record_id} onChange={e => setF({ ...f, record_id: e.target.value })} placeholder="paste an id" className={inp + " font-mono"} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">User Email</div>
            <input data-testid="audit-f-user" value={f.user_email} onChange={e => setF({ ...f, user_email: e.target.value })} placeholder="ops@…" className={inp} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Verb</div>
            <select data-testid="audit-f-verb" value={f.verb} onChange={e => setF({ ...f, verb: e.target.value })} className={inp}>
              <option value="">All</option>
              <option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
            </select>
          </div>
          <button data-testid="audit-apply" onClick={load} className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center justify-center gap-2">
            <MagnifyingGlassIcon size={13} weight="bold" />Apply
          </button>
        </div>

        {/* Table */}
        <div className="tactical-card p-0 overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex justify-between">
            <span>{rows.length} entries · newest first</span>
            {loading && <span>Loading…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-center w-20">Verb</th>
                  <th className="px-3 py-2 text-left">Collection</th>
                  <th className="px-3 py-2 text-left">Path</th>
                  <th className="px-3 py-2 text-center w-16">Status</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-center w-14">View</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-zinc-500 text-xs">No audit entries match the filter.</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`audit-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-3 py-2 font-mono text-zinc-400 whitespace-nowrap" title={`UTC: ${r.ts}`}>{fmtIST(r.ts)} <span className="text-[9px] text-zinc-600">IST</span></td>
                    <td className="px-3 py-2 text-zinc-300 truncate max-w-[180px]">{r.user_email || <span className="text-zinc-600">—</span>}<div className="text-[10px] text-zinc-500">{r.user_role || ""}</div></td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[9px] px-2 py-0.5 rounded-sm font-mono uppercase tracking-widest border ${verbColor(r.verb)}`}>{r.verb}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-purple-300">{r.collection || "—"}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400 truncate max-w-[320px]">{r.path}</td>
                    <td className={`px-3 py-2 text-center font-mono tabular-nums ${r.status_code >= 400 ? "text-red-400" : "text-emerald-300"}`}>{r.status_code}</td>
                    <td className="px-3 py-2 text-zinc-300 truncate max-w-[240px]">{r.reason || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => setView(r)} data-testid={`audit-view-${i}`} className="p-1.5 rounded-sm hover:bg-sky-500/10 text-sky-400"><EyeIcon size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Dialog · before / after diff */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="audit-view-dialog" description="Audit log action — inspect full before / after payload of an entry.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClipboardTextIcon size={16} weight="bold" className="text-[var(--accent)]" />
              Audit Entry · <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${verbColor(view?.verb)}`}>{view?.verb}</span>
              <span className="font-mono text-xs text-zinc-400">{view?.path}</span>
            </DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-3 pt-2 text-xs">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div><div className="overline mb-0.5">When (IST)</div><div className="font-mono" title={`UTC: ${view.ts}`}>{fmtIST(view.ts)}</div></div>
                <div><div className="overline mb-0.5">User</div><div>{view.user_email} <span className="text-zinc-500">({view.user_role})</span></div></div>
                <div><div className="overline mb-0.5">Record ID</div><div className="font-mono text-purple-300 break-all">{view.record_id || "—"}</div></div>
                <div><div className="overline mb-0.5">Status</div><div className={`font-mono ${view.status_code >= 400 ? "text-red-400" : "text-emerald-300"}`}>{view.status_code}</div></div>
                <div className="col-span-2"><div className="overline mb-0.5">Reason</div><div>{view.reason || "—"}</div></div>
              </div>
              {(() => {
                const diff = computeDiff(view.before, view.after);
                return (
                  <div className="space-y-2" data-testid="audit-diff-block">
                    <div className="flex items-center justify-between">
                      <div className="overline text-[var(--accent)]">Changed Fields · {diff.length}</div>
                      <button
                        data-testid="audit-toggle-raw"
                        onClick={() => setShowRaw(v => !v)}
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[var(--border)] rounded-sm hover:bg-[var(--bg-2)]"
                      >
                        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
                      </button>
                    </div>

                    {diff.length === 0 && (
                      <div className="text-zinc-500 text-xs italic border border-dashed border-[var(--border)] rounded-sm p-3" data-testid="audit-diff-empty">
                        No field-level changes detected. This is common for lifecycle-only actions (approve, reject, cancel, issue) where the state flip is captured under Reason / Status, or for endpoints that don't persist a document (like adjust-qty on a specific line).
                      </div>
                    )}

                    {diff.length > 0 && (
                      <>
                        {/* Plain-English summary — one sentence per change. */}
                        <ul className="border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/30 p-2 space-y-1" data-testid="audit-diff-summary">
                          {diff.map((d, i) => (
                            <li key={i} className="text-[11px] text-zinc-200 leading-relaxed">
                              <span className={`inline-block w-14 text-[9px] font-mono uppercase tracking-widest text-center mr-2 py-0.5 rounded-sm ${
                                d.kind === "added" ? "bg-emerald-500/15 text-emerald-300" :
                                d.kind === "removed" ? "bg-red-500/15 text-red-300" :
                                "bg-sky-500/15 text-sky-300"
                              }`}>{d.kind}</span>
                              {changeSentence(d)}
                            </li>
                          ))}
                        </ul>

                        {/* Full before/after table below for detail-oriented reviewers. */}
                        <div className="border border-[var(--border)] rounded-sm overflow-hidden" data-testid="audit-diff-table">
                        <table className="w-full text-[11px]">
                          <thead className="bg-[var(--bg-2)]/60 text-[9px] uppercase tracking-widest text-zinc-500">
                            <tr>
                              <th className="px-3 py-1.5 text-left w-52">Field</th>
                              <th className="px-3 py-1.5 text-left">Before</th>
                              <th className="px-3 py-1.5 text-left">After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diff.map((d, i) => (
                              <tr key={i} data-testid={`audit-diff-row-${i}`} className="border-t border-[var(--border)] align-top">
                                <td className="px-3 py-1.5 font-mono text-purple-300">{labelFor(String(d.key).split(" · ")[0])}<span className="text-zinc-500">{String(d.key).includes(" · ") ? " · " + String(d.key).split(" · ").slice(1).join(" · ") : ""}</span></td>
                                <td className="px-3 py-1.5 bg-red-500/5 text-red-200">{fmtVal(d.before)}</td>
                                <td className="px-3 py-1.5 bg-emerald-500/5 text-emerald-200">{fmtVal(d.after)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </>
                    )}

                    {showRaw && (
                      <div className="grid grid-cols-2 gap-3 pt-2" data-testid="audit-raw-block">
                        <div>
                          <div className="overline mb-1 text-red-300">Before (raw)</div>
                          <pre className="bg-red-500/5 border border-red-500/30 rounded-sm p-2 text-[10px] font-mono whitespace-pre-wrap max-h-64 overflow-auto">{view.before ? JSON.stringify(view.before, null, 2) : "—"}</pre>
                        </div>
                        <div>
                          <div className="overline mb-1 text-emerald-300">After (raw)</div>
                          <pre className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-2 text-[10px] font-mono whitespace-pre-wrap max-h-64 overflow-auto">{view.after ? JSON.stringify(view.after, null, 2) : "—"}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
